/**
 * Destino do link do e-mail: `/redefinir-senha?token=…`.
 *
 * O token nunca sai da URL para lugar nenhum além do POST — não vai para
 * localStorage nem para o histórico do app. Se a API recusar (expirado, já
 * usado, inventado), a mensagem manda pedir outro em vez de tentar explicar
 * qual dos três foi: para quem está na tela, o remédio é o mesmo.
 */
import { SENHA_MAX, SENHA_MIN, mensagemSenhaInvalida } from '@simpleadm/shared-types';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CampoSenha } from '@/components/CampoSenha';
import { MolduraAcesso } from '@/components/MolduraAcesso';
import { RequisitosSenha } from '@/components/RequisitosSenha';
import { ApiError, apiFetch } from '@/lib/api';



function mensagemDaApi(e: ApiError): string | null {
  try {
    const parsed = JSON.parse(e.body) as { message?: string | string[] };
    const m = parsed.message;
    return Array.isArray(m) ? (m[0] ?? null) : (m ?? null);
  } catch {
    return null;
  }
}

export function RedefinirSenhaPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    const problema = mensagemSenhaInvalida(nova);
    if (problema) return setErro(problema);
    if (nova !== confirmacao) return setErro('A confirmação não bate com a senha.');
    setEnviando(true);
    try {
      await apiFetch<void>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: nova }),
        skipAuth: true,
      });
      setFeito(true);
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        setErro(mensagemDaApi(e) ?? 'Link inválido ou expirado. Peça um novo.');
      } else if (e instanceof ApiError && e.status === 429) {
        setErro('Muitas tentativas. Aguarde alguns minutos e tente de novo.');
      } else {
        setErro('Não foi possível salvar agora. Tente de novo em instantes.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <MolduraAcesso titulo="Criar nova senha" icone={<KeyRound className="h-5 w-5" />}>
      {!token && (
        <div className="space-y-3 text-sm">
          <p>Este endereço não tem o código do link. Abra o link exatamente como veio no e-mail.</p>
          <Link
            to="/esqueci-senha"
            className="block text-center text-xs underline-offset-2 hover:underline"
          >
            Pedir um novo link
          </Link>
        </div>
      )}

      {token && feito && (
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <span>Senha alterada. Suas sessões antigas foram encerradas — entre de novo.</span>
          </p>
          <Link
            to="/login"
            className="block w-full rounded-md bg-primary py-2.5 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Ir para o login
          </Link>
        </div>
      )}

      {token && !feito && (
        <form onSubmit={enviar} className="space-y-4">
          <CampoSenha
            label="Nova senha"
            required
            minLength={SENHA_MIN}
            maxLength={SENHA_MAX}
            autoComplete="new-password"
            autoFocus
            value={nova}
            onChange={(e) => setNova(e.target.value)}
            disabled={enviando}
            abaixo={<RequisitosSenha senha={nova} />}
          />
          <CampoSenha
            label="Repita a nova senha"
            required
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            disabled={enviando}
          />
          {erro && (
            <p role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          )}
          <button
            type="submit"
            disabled={enviando}
            className="w-full rounded-md bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {enviando ? 'Salvando…' : 'Salvar nova senha'}
          </button>
        </form>
      )}
    </MolduraAcesso>
  );
}
